import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Printer, Ban, X } from 'lucide-react';
import { formatMoney } from '@/lib/format';

type Props = {
  consecutivo: string;
  total: number;
  monedaCodigo: string;
  itemsCount: number;
  /**
   * Sprint 99 POS.5 — consecutivo oficial del servidor. Si está disponible
   * se muestra al lado del local con la coletilla "(servidor)" para que el
   * cajero vea el numero que el sistema reconoce, sin perder de vista el
   * numero que imprimio en el ticket.
   */
  numeroFacturaServidor?: string;
  onReimprimir?: () => void;
  onAnular?: () => void;
  onDismiss?: () => void;
  autoDismissMs?: number;
};

export function PostSaleStrip({
  consecutivo,
  total,
  monedaCodigo,
  itemsCount,
  numeroFacturaServidor,
  onReimprimir,
  onAnular,
  onDismiss,
  autoDismissMs = 12000,
}: Props) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    timerRef.current = setTimeout(() => setLeaving(true), autoDismissMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [paused, autoDismissMs]);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => onDismiss?.(), 200);
    return () => clearTimeout(t);
  }, [leaving, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={
        'border-success-soft bg-success-soft text-success flex items-center gap-3 rounded-md border px-4 py-3 ' +
        (leaving ? 'animate-fade-out' : 'animate-fade-in')
      }
    >
      <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="tnum font-mono text-[13px] font-medium">{consecutivo || '—'}</span>
        {numeroFacturaServidor && numeroFacturaServidor !== consecutivo ? (
          <span
            className="tnum font-mono text-[11px] opacity-80"
            title="Consecutivo oficial que el servidor asigno al subir la venta"
          >
            servidor: {numeroFacturaServidor}
          </span>
        ) : null}
        <span className="tnum font-semibold">{formatMoney(total, monedaCodigo)}</span>
        <span className="text-xs opacity-80">
          {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {onReimprimir && (
          <button
            type="button"
            onClick={onReimprimir}
            aria-label="Reimprimir ticket"
            className="text-success flex h-11 w-11 items-center justify-center rounded-md transition-colors hover:bg-[rgba(4,120,87,0.12)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
          >
            <Printer className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {onAnular && (
          <button
            type="button"
            onClick={onAnular}
            aria-label="Anular esta venta"
            className="text-danger hover:bg-danger-soft flex h-11 w-11 items-center justify-center rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
          >
            <Ban className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setLeaving(true)}
          aria-label="Cerrar aviso"
          className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
